/**
 * AppDistributionService
 *
 * Private app distribution + redemption codes (`CCMS.Api.ApplicationCatalogue`,
 * migration 194). CCMS mobile apps are not searchable in the public stores: Apple ships
 * via Apple Business Manager custom-app redemption codes, Android via an install link
 * (Google Play has no promo codes for free apps).
 *
 * Backs two surfaces:
 *  - the admin distribution + code-pool screens (`/admin/app-catalogue/*`)
 *  - the user's "Get the Apps" download page (`/app/apps`)
 *
 * Routes (gateway prefix `application-catalogue`):
 *  ADMIN — /api/catalogue/admin/distribution
 *   - GET    /lookups
 *   - GET    /?appId=                                   — config + pool stats
 *   - PUT    /{appId}/{platformId}                      — upsert
 *   - PATCH  /{distributionId}/toggle?enabled=
 *   - DELETE /{distributionId}
 *   - GET    /stats?appId=
 *   - POST   /{distributionId}/codes/batch              — bulk upload
 *   - GET    /{distributionId}/batches
 *   - GET    /{distributionId}/codes                    — paginated, masked by default
 *   - POST   /{distributionId}/codes/assign             — claim N for a user (409 if empty)
 *   - POST   /codes/{codeId}/assign | unassign | revoke | redeem
 *   - PATCH  /codes/{codeId}/status
 *   - GET    /codes/{codeId}/events
 *  USER — /api/catalogue/me
 *   - GET    /apps                                      — the whole download page
 *   - POST   /codes/{codeId}/redeem                     — one-way
 *
 * A redeemed code is TERMINAL: it cannot be revoked, unassigned or reset. Those calls
 * answer 409. The UI should offer no actions on a used code — only the fact that it was.
 */
import { BaseService } from '../base/BaseService';
export class AppDistributionService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? AppDistributionService.DEFAULT_SERVICE_PATH,
            basePath: AppDistributionService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== USER — the download page ====================
    /**
     * The signed-in user's whole download page in one call: every app they are entitled
     * to, how to install it on each platform, and the codes they personally hold.
     * GET /api/catalogue/me/apps
     */
    async getMyApps() {
        const response = await this.getMyAppsRaw();
        return response.data ?? [];
    }
    getMyAppsRaw() {
        return this.http.get(this.buildUrl('me/apps'));
    }
    /**
     * Tell CCMS you used your code. One-way: Apple gives us no redemption callback, so
     * this button is what makes a code "used".
     * POST /api/catalogue/me/codes/{codeId}/redeem
     *
     * @returns true when it flipped. Rejects with 409 if the code was already used or
     *          revoked, and 404 if it isn't yours — both are terminal, nothing to retry.
     */
    async redeemMyCode(codeId) {
        const response = await this.redeemMyCodeRaw(codeId);
        return response.success;
    }
    redeemMyCodeRaw(codeId) {
        return this.http.post(this.buildUrl(`me/codes/${codeId}/redeem`));
    }
    // ==================== ADMIN — distribution config ====================
    /** Platforms, distribution methods, code statuses, event types. GET /admin/distribution/lookups */
    async getLookups() {
        const response = await this.getLookupsRaw();
        if (!response.data) {
            throw new Error(response.message ?? 'Failed to load distribution lookups');
        }
        return response.data;
    }
    getLookupsRaw() {
        return this.http.get(this.adminUrl('lookups'));
    }
    /** Distribution config + pool stats. GET /admin/distribution?appId= */
    async getDistributions(appId) {
        const response = await this.getDistributionsRaw(appId);
        return response.data ?? [];
    }
    getDistributionsRaw(appId) {
        const url = this.buildUrlWithParams('admin/distribution', appId ? { appId } : undefined);
        return this.http.get(url);
    }
    /**
     * Create or replace how one app is installed on one platform.
     * PUT /admin/distribution/{appId}/{platformId}
     */
    async upsertDistribution(appId, platformId, request) {
        const response = await this.upsertDistributionRaw(appId, platformId, request);
        if (!response.data) {
            throw new Error(response.message ?? 'Failed to save the distribution');
        }
        return response.data;
    }
    upsertDistributionRaw(appId, platformId, request) {
        const url = this.adminUrl(`${encodeURIComponent(appId)}/${platformId}`);
        return this.http.put(url, request);
    }
    /** Show / hide a platform on the user download page. PATCH /admin/distribution/{id}/toggle */
    async toggleDistribution(distributionId, enabled) {
        const response = await this.toggleDistributionRaw(distributionId, enabled);
        return response.success;
    }
    toggleDistributionRaw(distributionId, enabled) {
        const url = this.buildUrlWithParams(`admin/distribution/${distributionId}/toggle`, { enabled });
        return this.http.patch(url);
    }
    /** Removes the config AND its code pool. DELETE /admin/distribution/{id} */
    async deleteDistribution(distributionId) {
        const response = await this.deleteDistributionRaw(distributionId);
        return response.success;
    }
    deleteDistributionRaw(distributionId) {
        return this.http.delete(this.adminUrl(String(distributionId)));
    }
    /** Pool health, incl. the low-stock flag. GET /admin/distribution/stats?appId= */
    async getPoolStats(appId) {
        const response = await this.getPoolStatsRaw(appId);
        return response.data ?? [];
    }
    getPoolStatsRaw(appId) {
        const url = this.buildUrlWithParams('admin/distribution/stats', appId ? { appId } : undefined);
        return this.http.get(url);
    }
    // ==================== ADMIN — the code pool ====================
    /**
     * Bulk-upload redemption codes. Parse the CSV / pasted list in the browser and post
     * the raw strings — no file reaches the server. Re-uploading the same batch is a
     * no-op: the result reports `duplicates`, it does not fail.
     * POST /admin/distribution/{distributionId}/codes/batch
     */
    async uploadCodeBatch(distributionId, request) {
        const response = await this.uploadCodeBatchRaw(distributionId, request);
        if (!response.data) {
            throw new Error(response.message ?? 'Failed to upload the codes');
        }
        return response.data;
    }
    uploadCodeBatchRaw(distributionId, request) {
        const url = this.adminUrl(`${distributionId}/codes/batch`);
        return this.http.post(url, request);
    }
    /** Upload history. GET /admin/distribution/{distributionId}/batches */
    async getBatches(distributionId) {
        const response = await this.getBatchesRaw(distributionId);
        return response.data ?? [];
    }
    getBatchesRaw(distributionId) {
        return this.http.get(this.adminUrl(`${distributionId}/batches`));
    }
    /**
     * The pool, paginated. Codes come back in full (they are not secrets), each with its
     * one-tap Apple redeem link.
     * GET /admin/distribution/{distributionId}/codes
     */
    getCodesRaw(distributionId, options) {
        const url = this.buildUrlWithParams(`admin/distribution/${distributionId}/codes`, {
            statusId: options?.statusId,
            search: options?.search,
            skip: options?.skip ?? 0,
            take: options?.take ?? 50,
        });
        return this.http.get(url);
    }
    /** The audit trail for one code. GET /admin/distribution/codes/{codeId}/events */
    async getCodeEvents(codeId) {
        const response = await this.getCodeEventsRaw(codeId);
        return response.data ?? [];
    }
    getCodeEventsRaw(codeId) {
        return this.http.get(this.adminUrl(`codes/${codeId}/events`));
    }
    // ==================== ADMIN — assignment ====================
    /**
     * Claim `count` codes from the pool for one user, and email them.
     * POST /admin/distribution/{distributionId}/codes/assign
     *
     * Rejects with **409** when the pool is empty — the cue to upload more codes.
     * When the pool runs out mid-way it returns what it managed to claim.
     */
    async assignCodes(distributionId, request) {
        const response = await this.assignCodesRaw(distributionId, request);
        return response.data ?? [];
    }
    assignCodesRaw(distributionId, request) {
        const url = this.adminUrl(`${distributionId}/codes/assign`);
        return this.http.post(url, request);
    }
    /** Hand out one specific code. POST /admin/distribution/codes/{codeId}/assign */
    assignSpecificCodeRaw(codeId, request) {
        return this.http.post(this.adminUrl(`codes/${codeId}/assign`), request);
    }
    /**
     * Return an UNUSED code to the pool.
     * **409 if it has already been redeemed** — a used code is spent, there is nothing
     * to take back.
     */
    async unassignCode(codeId, note) {
        const response = await this.unassignCodeRaw(codeId, note);
        return response.success;
    }
    unassignCodeRaw(codeId, note) {
        return this.http.post(this.adminUrl(`codes/${codeId}/unassign`), { note });
    }
    /**
     * Burn an UNUSED code so nobody can use it.
     * **409 if it has already been redeemed** — you cannot un-ring that bell.
     */
    async revokeCode(codeId, note) {
        const response = await this.revokeCodeRaw(codeId, note);
        return response.success;
    }
    revokeCodeRaw(codeId, note) {
        return this.http.post(this.adminUrl(`codes/${codeId}/revoke`), { note });
    }
    /**
     * Admin correction — e.g. Apple rejected the code, so mark it Invalid.
     * Cannot move a code out of a terminal state, and cannot force one back to Available.
     */
    async setCodeStatus(codeId, request) {
        const response = await this.setCodeStatusRaw(codeId, request);
        return response.success;
    }
    setCodeStatusRaw(codeId, request) {
        return this.http.patch(this.adminUrl(`codes/${codeId}/status`), request);
    }
    /** Mark a code used on the holder's behalf ("they told me it worked"). One-way. */
    async markCodeRedeemed(codeId) {
        const response = await this.markCodeRedeemedRaw(codeId);
        return response.success;
    }
    markCodeRedeemedRaw(codeId) {
        return this.http.post(this.adminUrl(`codes/${codeId}/redeem`));
    }
    // ==================== helpers ====================
    adminUrl(endpoint) {
        return this.buildUrl(`admin/distribution/${endpoint}`);
    }
}
AppDistributionService.DEFAULT_SERVICE_PATH = 'application-catalogue';
AppDistributionService.BASE_PATH = 'api/catalogue';
//# sourceMappingURL=AppDistributionService.js.map